import { z } from 'zod';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

export const SUPPORTED_LOCALES = ['en', 'zh-CN', 'ko', 'ja'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const updateLanguageInput = z.object({
  language: z.enum(SUPPORTED_LOCALES),
});
export type UpdateLanguageInput = z.infer<typeof updateLanguageInput>;

export const VALID_TIMEZONES = new Set(
  typeof Intl !== 'undefined' && Intl.supportedValuesOf
    ? [...Intl.supportedValuesOf('timeZone'), 'UTC']
    : ['UTC']
);

/**
 * A zone this server can work in. `supportedValuesOf` lists only the ICU build's
 * CANONICAL names, and builds differ: a browser offers `Asia/Kolkata` and
 * `Europe/Kyiv` where Node lists `Asia/Calcutta` and `Europe/Kiev`. Any name
 * `Intl` itself accepts is a real IANA zone (or link) every reader of the
 * stored value can project through, so it is accepted too — otherwise a family
 * in India could never save their timezone from the Profile picker.
 */
export function isValidTimezone(val: string): boolean {
  if (VALID_TIMEZONES.has(val)) return true;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: val });
    return true;
  } catch {
    return false;
  }
}

/**
 * The US IANA zones, for the "has this account actually answered?" worklist
 * (EIFO report #199, issue #767).
 *
 * `users.timezone` is NOT NULL DEFAULT 'America/New_York' and registration never
 * supplies one, so an Overseas family that never opened the setting is sitting
 * on a US zone. `timezone_set_at IS NULL` catches the ones nobody ever asked;
 * this set catches the ones who saved the pre-filled default without changing
 * it, which `timezone_set_at` alone would count as answered.
 *
 * Deliberately an explicit list rather than a `America/%` prefix test: that
 * prefix also matches Toronto, Mexico City and São Paulo, which are not US
 * zones and would put the wrong families on an admin's worklist.
 */
export const US_TIMEZONES = [
  'America/New_York',
  'America/Detroit',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

export type UsTimezone = (typeof US_TIMEZONES)[number];

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one symbol');

/**
 * Phone field used by the register form and any other client-side validation.
 *
 * - Refines via libphonenumber-js's `isValidPhoneNumber` (loose default-country)
 * - Transforms to E.164 via `parsePhoneNumber(...).number` so downstream service
 *   code receives a guaranteed E.164 string and never has to re-parse.
 *
 * Resolves F1.
 */
export const phoneSchema = z
  .string()
  .trim()
  .min(1, 'Phone number is required')
  .max(32, 'Phone number is too long')
  .refine((val) => isValidPhoneNumber(val, 'US'), 'Please enter a valid phone number')
  .transform((val) => {
    const parsed = parsePhoneNumber(val, 'US');
    if (!parsed || !parsed.number) {
      // Dead code: refine() above blocks this, but the type narrowing
      // requires us to throw rather than return undefined.
      throw new Error('unreachable: refine should block this');
    }
    return parsed.number;
  });

export const createUserSchema = z
  .object({
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, 'Email is required')
      .max(200, 'Email must be at most 200 characters')
      .email('Please enter a valid email address'),
    phone: phoneSchema,
    password: passwordSchema,
    confirmPassword: z
      .string()
      .min(1, 'Please confirm your password')
      .max(128),
    firstName: z
      .string()
      .trim()
      .min(1, 'First name is required')
      .max(100, 'First name must be at most 100 characters'),
    lastName: z
      .string()
      .trim()
      .min(1, 'Last name is required')
      .max(100, 'Last name must be at most 100 characters'),
    consentAcknowledged: z.literal(true, {
      message:
        'You must agree to the Terms and Privacy Policy to create an account',
    }),
    /**
     * Honeypot field — INTENTIONALLY accepts any string (no .max(0)). The
     * route handler checks `honeypot.length > 0` and silently returns a
     * fake-success response. A `.max(0)` constraint here would cause zod
     * to REJECT non-empty values at parse time, making the route-handler
     * honeypot check unreachable. Resolves F24.
     */
    honeypot: z.string().optional(),
    turnstileToken: z.string().min(1, 'Captcha verification required'),
    /**
     * The program the visitor chose on the register form (Program Selection at
     * Signup). Optional: absent means "resolve it from cookie/locale/default".
     *
     * ⚠️ It belongs on the BASE object, not on an `.extend()` of it, because the
     * register form's `zodResolver` parses against THIS schema — an unknown key
     * would be stripped from `data` before `fetch` ever sends it.
     *
     * Bounds match `program.schema.ts › createProgramInput.slug` deliberately.
     * This is the one field the spec designates a security boundary; a looser
     * rule here than the one that mints slugs is a gap, not a convenience.
     * An unknown or non-selectable slug is IGNORED server-side rather than
     * rejected, so a stale client can never be locked out of registering.
     */
    programSlug: z
      .string()
      .trim()
      .max(60)
      .regex(/^[a-z0-9-]+$/, 'Invalid program')
      .optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateProfileInput = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, 'First name is required')
    .max(100, 'First name must be at most 100 characters'),
  lastName: z
    .string()
    .trim()
    .min(1, 'Last name is required')
    .max(100, 'Last name must be at most 100 characters'),
  // Optional: a parent's Profile saves the name on its own row. Leaving it out
  // keeps the stored zone AND its "confirmed" stamp untouched — a name edit is
  // not a timezone answer. Staff forms still send all three.
  timezone: z
    .string()
    .min(1, 'Timezone is required')
    .refine(isValidTimezone, 'Invalid timezone')
    .optional(),
});

export type UpdateProfileInput = z.infer<typeof updateProfileInput>;

/**
 * Update the account's mobile number from profile settings. Reuses phoneSchema
 * (refine + E.164 transform). Changing the number invalidates any prior phone
 * verification server-side, so the user must re-verify via OTP.
 */
export const updatePhoneInput = z.object({
  phone: phoneSchema,
});

export type UpdatePhoneInput = z.infer<typeof updatePhoneInput>;

export const updateEmailInput = z.object({
  newEmail: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  currentPassword: z
    .string()
    .min(1, 'Current password is required')
    .max(128),
});

export type UpdateEmailInput = z.infer<typeof updateEmailInput>;

export const changePasswordInput = z.object({
  currentPassword: z
    .string()
    .min(1, 'Current password is required')
    .max(128),
  newPassword: passwordSchema,
});

export type ChangePasswordInput = z.infer<typeof changePasswordInput>;
